import { useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Collapse } from 'src/components/ui';
import { AuthContext } from '../context/AuthContext';
import EmptyStateError from './Icons/EmptyStates/EmptyStateError';
import { getRecentLogs } from './consoleCapture';
import Icon from 'src/components/Icon';

function extractErrorInfo(error) {
  if (!error) return { name: 'Unknown', message: 'An unknown error occurred.', stack: '' };
  const name = error.name ?? error.constructor?.name ?? 'Error';
  let message = error.message;
  if (!message && error.response?.data) {
    const d = error.response.data;
    message = typeof d === 'string' ? d : d.message ?? d.detail ?? JSON.stringify(d);
  }
  if (!message) message = String(error);
  return { name, message: message || 'No message provided.', stack: error.stack ?? '' };
}

function classifyError(error) {
  const { name, message } = extractErrorInfo(error);
  const combined = `${name} ${message} ${error?.code ?? ''}`;
  if (name === 'ChunkLoadError' || /loading (css )?chunk/i.test(combined)) return 'chunk';
  if (/failed to fetch|network error|err_network|networkerror/i.test(combined)) return 'network';
  if (name === 'TypeError') return 'type';
  return 'generic';
}

function downloadDump({ error, componentStack, auth }) {
  const { name, message, stack } = extractErrorInfo(error);
  const data = {
    timestamp: new Date().toISOString(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    error: { name, message, stack },
    componentStack: componentStack ?? null,
    user: auth?.user ? { email: auth.user.email, organizationalUnitId: auth.user.user?.actualOrganizationalUnitId } : null,
    recentLogs: getRecentLogs(),
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `error-report-${Date.now()}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const ErrorFallback = ({ error, componentStack, resetError }) => {
  const theme = useTheme();
  const auth = useContext(AuthContext);
  const history = useHistory();
  const [showDetails, setShowDetails] = useState(false);

  const handleGoHome = () => {
    resetError?.();
    history.push('/');
  };

  const { name, message, stack } = useMemo(() => extractErrorInfo(error), [error]);
  const kind = useMemo(() => classifyError(error), [error]);

  const headline = useMemo(() => {
    if (kind === 'chunk') return t`The page is out of date`;
    if (kind === 'network') return t`Network error`;
    if (kind === 'type') return t`Unexpected data error`;
    return t`Something went wrong`;
  }, [kind]);

  const detail = useMemo(() => {
    if (kind === 'chunk') return t`A new version was deployed while you were using the app. Reloading will fetch the latest version.`;
    if (kind === 'network') return t`A request could not be completed. Check your network connection, then reload the page.`;
    if (kind === 'type') return t`The application encountered unexpected data. Reload the page to continue.`;
    return t`An unexpected error occurred. Reload the page to try again. If the problem persists, download the error report and share it with support.`;
  }, [kind]);

  const hasDetails = !!(stack || componentStack);

  return (
    <div
      className="error-fallback flex flex-col items-center text-center h-full w-full"
      style={{ padding: '24px 16px', justifyContent: 'center', position: 'relative', top: '-30px' }}
    >
      <EmptyStateError width={200} height={200} color={theme.dangerMain} />

      <p className="text-danger font-bold mb-2" style={{ maxWidth: '340px', position: 'relative', top: -20 }}>
        {headline}
      </p>

      <p className="text-muted mb-2" style={{ maxWidth: '650px', fontSize: '0.875rem' }}>
        {detail}
      </p>

      <p className="text-muted mb-4" style={{ maxWidth: '650px', fontSize: '0.875rem' }}>
        <code>{message}</code>
      </p>

      <div className="flex items-center flex-wrap justify-center" style={{ gap: '8px' }}>
        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={handleGoHome}>
          <Icon icon="House" className="mr-1" />
          <Trans>Go to home</Trans>
        </button>
        <button className="btn btn-danger btn-sm" type="button" onClick={() => window.location.reload()}>
          <Trans>Reload page</Trans>
        </button>
        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => downloadDump({ error, componentStack, auth })}>
          <Icon icon="Download" className="mr-1" />
          <Trans>Download error report</Trans>
        </button>
      </div>

      {hasDetails && (
        <div className="mt-3">
          <button className="btn btn-link btn-sm text-muted p-0" type="button" onClick={() => setShowDetails(v => !v)} style={{ fontSize: '0.8rem' }}>
            {showDetails ? <Trans>Hide details</Trans> : <Trans>Show details</Trans>}
          </button>
          <Collapse isOpen={showDetails}>
            <div
              className="error-fallback-stack text-left text-muted mt-2"
              style={{
                fontSize: '0.72rem',
                maxWidth: '680px',
                maxHeight: '280px',
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                background: 'var(--bs-light, #f8f9fa)',
                padding: '10px 14px',
                borderRadius: '6px',
              }}
            >
              {name && (
                <div>
                  <strong>{name}</strong>
                </div>
              )}
              {stack && <div className="mt-1">{stack}</div>}
              {componentStack && (
                <>
                  <div className="mt-2">
                    <strong>
                      <Trans>Component stack</Trans>
                    </strong>
                  </div>
                  <div>{componentStack}</div>
                </>
              )}
            </div>
          </Collapse>
        </div>
      )}
    </div>
  );
};

export default ErrorFallback;
